import { createContext, useContext, type KeyboardEvent } from "react";

export const InMenu = createContext(false);

/// A row inside a labelled menu is a `menuitem` reached by the arrow keys —
/// the menu decides, so no row can forget.
export const useMenuRowProps = (): {
  role?: "menuitem";
  tabIndex?: number;
} => (useContext(InMenu) ? { role: "menuitem", tabIndex: -1 } : {});

/// Only the rows of `panel` itself — an open submenu's rows sit inside it in the
/// DOM but navigate on their own.
export const rowsOf = (panel: HTMLElement): HTMLElement[] =>
  Array.from(panel.querySelectorAll<HTMLElement>('[role="menuitem"]')).filter(
    (el) => el.closest('[role="menu"]') === panel,
  );

/// Wraps at both ends, and enters from whichever end the arrow key came from.
/// Disabled rows stay in the cycle: skipping them hides that they exist.
const moveFocus = (panel: HTMLElement, delta: number): void => {
  const rows = rowsOf(panel);
  if (rows.length === 0) return;
  const current = rows.indexOf(document.activeElement as HTMLElement);
  const next =
    current === -1
      ? delta > 0
        ? 0
        : rows.length - 1
      : (current + delta + rows.length) % rows.length;
  rows[next].focus();
};

/// True if the panel handled the key itself.
export const handleRowNavigation = (
  event: KeyboardEvent,
  panel: HTMLElement,
): boolean => {
  const rows = rowsOf(panel);
  switch (event.key) {
    case "ArrowDown":
      moveFocus(panel, 1);
      return true;
    case "ArrowUp":
      moveFocus(panel, -1);
      return true;
    case "Home":
      rows[0]?.focus();
      return true;
    case "End":
      rows[rows.length - 1]?.focus();
      return true;
    default:
      return false;
  }
};
